import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { Variants } from 'framer-motion';
import productsData from '../data/products.json';
import { isVisibleProduct } from '../data/hiddenProducts';

// Simple Arrow Icon Component
const ArrowIcon = ({ className, direction = 'right' }: { className?: string; direction?: 'left' | 'right' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path d={direction === 'left' ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
  </svg>
);

const ProductDetailsPage: React.FC = () => {
  const allProducts = productsData.filter(isVisibleProduct);
  const navigate = useNavigate();
  const location = useLocation();

  const [currentProduct, setCurrentProduct] = useState<any>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [otherProductsStartIndex, setOtherProductsStartIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- LOGIC INTEGRATION ---
  
  const handleEmail = () => {
    const productName = currentProduct.name;
    const subject = 'Requesting for quote';
    const body = `Can I have a quote for the following details:\nProduct name: ${productName}\nQuantity:\nLocation:\nMobile Number:`;
    const mailtoUrl = `mailto:sales@rkandkimport.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    if (typeof (window as any).gtag_report_conversion === 'function') {
      (window as any).gtag_report_conversion(mailtoUrl);
    } else {
      window.location.href = mailtoUrl;
    }
    setIsModalOpen(false);
  };

  // --- END LOGIC ---

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productName = params.get('name');
    if (productName) {
      const found = allProducts.find((p) => p.name === decodeURIComponent(productName));
      if (found) {
        setCurrentProduct(found);
        setCurrentImageIndex(0);
      }
    }
    window.scrollTo(0, 0);
  }, [location.search, allProducts]);

  if (!currentProduct) return null;

  const selectProduct = (name: string) => {
    navigate(`/products/details?name=${encodeURIComponent(name)}`);
  };

  const productsToDisplay = allProducts.slice(otherProductsStartIndex, otherProductsStartIndex + 4);

  const itemVariants: Variants = {
    hidden: { y: 15, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white font-inter !text-black flex flex-col selection:bg-black selection:text-white">
      <main className="flex-grow px-6 sm:px-12 pb-24 pt-32 max-w-[1600px] mx-auto w-full">
        
        <div className="flex flex-col lg:flex-row gap-16">
          {/* IMAGE SECTION */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:w-3/5 flex flex-col items-center"
          >
            <div className="w-full aspect-square bg-[#fdfdfd] border border-gray-100 rounded-[40px] flex items-center justify-center p-12 mb-8 shadow-sm">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.1 }}
                  src={currentProduct.images[currentImageIndex]}
                  alt={currentProduct.name}
                  fetchPriority="high"
                  decoding="async"
                  className="max-w-full max-h-full object-contain"
                />
              </AnimatePresence>
            </div>
            
            {currentProduct.images.length > 1 && (
              <div className="flex gap-4">
                {currentProduct.images.map((img: string, idx: number) => (
                  <button 
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-20 h-20 rounded-2xl border-2 overflow-hidden transition-all bg-white ${
                      idx === currentImageIndex ? 'border-black' : 'border-gray-100 opacity-60'
                    }`}
                  >
                    <img src={img} loading="lazy" decoding="async" className="w-full h-full object-contain p-2" alt="" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* PRODUCT INFO & BROWSE */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:w-2/5 flex flex-col">
            <span className="text-sm font-black tracking-[0.3em] text-gray-400 uppercase mb-4 block">
              {currentProduct.brand || currentProduct.group} Professional
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase italic leading-[0.9] mb-6 !text-black">
              {currentProduct.name}
            </h1>
            <p className="text-xl text-gray-500 font-medium leading-relaxed mb-10 italic">
              {currentProduct.description}
            </p>

            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-transparent border-2 border-black !text-black px-10 py-5 rounded-full font-bold text-sm tracking-widest hover:!bg-black hover:!text-white transition-all duration-300 w-fit mb-16"
            >
              REQUEST A QUOTE
            </button>

            {/* Browse Other Products */}
            <div className="pt-8 border-t border-gray-100">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400">Discover More</h3>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setOtherProductsStartIndex(Math.max(0, otherProductsStartIndex - 1))}
                    className="p-3 border border-black/20 !text-black !bg-transparent rounded-full transition-all duration-300 hover:!bg-black hover:!text-white disabled:opacity-10 disabled:cursor-not-allowed"
                    disabled={otherProductsStartIndex === 0}
                  >
                    <ArrowIcon className="w-4 h-4" direction="left" />
                  </button>
                  <button 
                    onClick={() => setOtherProductsStartIndex(Math.min(allProducts.length - 4, otherProductsStartIndex + 1))}
                    className="p-3 border border-black/20 !text-black !bg-transparent rounded-full transition-all duration-300 hover:!bg-black hover:!text-white disabled:opacity-10 disabled:cursor-not-allowed"
                    disabled={otherProductsStartIndex >= allProducts.length - 4}
                  >
                    <ArrowIcon className="w-4 h-4" direction="right" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4">
                {productsToDisplay.map((product) => (
                  <div key={product.name} className="group cursor-pointer text-center" onClick={() => selectProduct(product.name)}>
                    <div className="aspect-[4/5] bg-[#f9f9f9] rounded-2xl p-4 mb-2 flex items-center justify-center group-hover:bg-white group-hover:shadow-xl transition-all border border-transparent group-hover:border-gray-100">
                      <img src={product.images[0]} loading="lazy" decoding="async" className="max-h-full object-contain" alt={product.name} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* SPECIFICATIONS GRID */}
        <section className="mt-32">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-6 mb-16"
          >
            <h2 className="text-xs font-black uppercase tracking-[0.4em] text-gray-500 whitespace-nowrap">Technical Matrix</h2>
            <div className="h-px w-full bg-gray-100" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(currentProduct.specs || currentProduct.details?.specifications || {}).map(([key, value], index) => (
              <motion.div 
                key={index}
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false }}
                className="bg-white border border-gray-100 p-8 rounded-[32px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-all duration-500 flex flex-col justify-between min-h-[160px]"
              >
                <span className="text-[14px] font-black uppercase tracking-widest text-gray-600 mb-4 block">
                  {typeof key === 'string' ? key.replace(/_/g, ' ') : `Spec ${index}`}
                </span>
                <span className="text-xl font-bold !text-black leading-tight">
                  {typeof value === 'object' && value !== null ? (value as any).value : String(value)}
                </span>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      {/* MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-md rounded-[40px] bg-white p-10 shadow-2xl text-center"
            >
              <h3 className="text-3xl font-bold text-black mb-2 uppercase italic tracking-tighter">
                Contact Us
              </h3>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                Connect with us regarding: <br/>
                <span className="text-black font-bold uppercase block mt-1">{currentProduct.name}</span>
              </p>

              <div className="space-y-6">
                {/* EMAIL OPTION */}
                <button
                  onClick={handleEmail}
                  className="w-full py-5 bg-black text-white rounded-3xl font-bold text-xs tracking-widest uppercase hover:bg-gray-800 transition-all active:scale-95 shadow-lg shadow-black/10"
                >
                  Send Inquiry via Email
                </button>

                {/* CALL US SECTION */}
                <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4">Direct Lines</h4>
                    <div className="space-y-4 text-left">
                        <div className="flex flex-col">
                            <span className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Operations</span>
                            <a href="tel:+639778361411" className="text-lg font-bold text-black hover:text-gray-600 transition-colors">+63 977 836 1411</a>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Marketing</span>
                            <a href="tel:+639778031411" className="text-lg font-bold text-black hover:text-gray-600 transition-colors">+63 977 803 1411</a>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[12px] font-black text-gray-400 uppercase tracking-widest">Sales</span>
                            <a href="tel:+639778021411" className="text-lg font-bold text-black hover:text-gray-600 transition-colors">+63 977 802 1411</a>
                        </div>
                    </div>
                </div>

                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full py-4 text-gray-400 text-[10px] font-bold uppercase tracking-[0.2em] mt-4 hover:text-black transition-colors"
                >
                  Close Window
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-[9px] text-gray-400 uppercase tracking-widest font-medium">
                  RK&K Import Cold Chain Solutions
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDetailsPage;